'use client';

import { RecommendationContext } from '@/configs/recommendations';
import { Nullable, type DTO } from '@/type-utils';
import { use, useEffect, useState } from 'react';
import { PageType } from '@/services/models/Page';
import { IProduct } from '@/services/models/Product';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import ProductService from '@/services/isomorphic/ProductService';
import RecommendationService from '@/services/isomorphic/RecommendationService';
import LoggerService from '@/services/isomorphic/LoggerService';
import { isNullish } from '@/utils/null-utils';
import { zip } from '@/utils/array-utils';
import { PageContext } from '../components/templates/context/PageContext';

/** `useRecommendedProducts` returning interface. */
export interface IUseRecommendedProducts {
  /** A list of recommended products. */
  products: Array<IProduct>;

  /** A list of ids that correspond to the slots for the recommended products. These should occur in the same order as the products. */
  slots: Array<string>;

  /** A title to display above the recommendations. */
  title: Nullable<string>;

  /** A parent title to display above the recommendations in tab mode. */
  parentTitle: Nullable<string>;

  /** Is the current recommendation context currently enabled. */
  isRecommendationEnabled: boolean;

  /** Is the current context set to slide on mobile, which is an alternate mobile style. */
  isSlideOnMobile: boolean;
}

/** Options for the {@link useRecommendedProducts} hook. */
export interface IUseRecommendedProductsOptions {
  /** The recommendation context to fetch products for. */
  context: RecommendationContext;

  /** The number of products to be fetched. */
  numberOfProducts?: number;

  /**
   * Pre-fetched recommended products. When rendering the `Custom404Page` from
   * the `GenericPage` the props `recommendedProducts` will be undefined since
   * it is fetched in a static manner. Therefore, we need to conditionally check
   * it before actually fetching recommended products again.
   */
  recommendedProducts?: Array<IProduct>;

  /**
   * Optional page-type-specific data for the DY page context
   * (e.g., the product SKU for PRODUCT pages).
   */
  contextData?: ReadonlyArray<string>;
}

/**
 * `useRecommendedProducts` fetches recommended products given a context.
 * @param options - The options for fetching recommended products.
 * @returns - A list of recommended products.
 */
export const useRecommendedProducts = ({
  context,
  numberOfProducts,
  recommendedProducts,
  contextData
}: IUseRecommendedProductsOptions): IUseRecommendedProducts => {
  const { isRecommendationEnabled, isSlideOnMobile } =
    RecommendationService.getContextConfig(context);

  const [products, setProducts] = useState(
    isRecommendationEnabled ? recommendedProducts : []
  );
  const [title, setTitle] = useState<string | null>(null);
  const [parentTitle, setParentTitle] = useState<string | null>(null);
  const [slots, setSlots] = useState<Array<string>>([]);

  const page = use(PageContext)?.page;

  const [stableContextData, setStableContextData] = useState(contextData);

  if (contextData?.join(',') !== stableContextData?.join(',')) {
    setStableContextData(contextData);
  }

  useEffect(
    function getRecommendedProducts() {
      let cancelled = false;
      if (!isRecommendationEnabled) return undefined;
      if (products !== undefined) return undefined;

      const fetchProducts = async (): Promise<void> => {
        const { productList, title, parentTitle, slotIds } =
          await RecommendationService.getRecommendations(
            context,
            page ?? {
              pageType: PageType.Unknown,
              url: EnvironmentService.url.origin
            },
            stableContextData
          );

        if (cancelled) return;

        const skusAndSlots = zip(productList, slotIds);

        const getProductPromises = skusAndSlots
          .slice(0, numberOfProducts)
          .map(async ([sku]) => {
            return ProductService.getProductDTO(sku).catch(() => {
              LoggerService.error(`Cannot find product with SKU: ${sku}`);
              return null;
            });
          });

        const recommendedSettledResult =
          await Promise.allSettled(getProductPromises);

        if (cancelled) return;

        // Filter out slot IDs corresponding to successfully fetched products
        const recommendedProdsAndSlots = recommendedSettledResult.reduce<
          Array<{ product: DTO<IProduct>; slotId: string }>
        >((prev, curr, index) => {
          if (curr.status === 'fulfilled' && !isNullish(curr.value)) {
            prev.push({ product: curr.value, slotId: skusAndSlots[index][1] });
          }
          return prev;
        }, []);

        const recommendedProds = recommendedProdsAndSlots.map(
          (item) => item.product
        );
        const slots = recommendedProdsAndSlots.map((item) => item.slotId);

        setTitle(title);
        setParentTitle(parentTitle);
        setProducts(recommendedProds);
        setSlots(slots);
      };

      // eslint-disable-next-line no-void -- Initiate the fetch call.
      void fetchProducts();

      return () => {
        cancelled = true;
      };
    },
    [
      context,
      stableContextData,
      isRecommendationEnabled,
      numberOfProducts,
      page,
      products
    ]
  );

  return {
    title,
    parentTitle,
    products: products ?? [],
    isRecommendationEnabled,
    isSlideOnMobile,
    slots
  };
};
